import { useEffect, useRef } from "react";

const DEFAULT_BLUR_TIMEOUT = 5 * 60 * 1000; // 5 minutes

type UseRefetchOnFocusOptions = {
  refetchOnBlur?: boolean;
  blurTimeoutMs?: number;
  /** When true, skip the mount-time refetch (caller already loaded initial data). */
  skipInitialFetch?: boolean;
};

export function useRefetchOnFocus(refetchFn: (isSyncing: boolean) => void, deps = [], options: UseRefetchOnFocusOptions = {}) {
  const { refetchOnBlur = false, blurTimeoutMs = DEFAULT_BLUR_TIMEOUT, skipInitialFetch = false } = options;
  const hasBlurred = useRef(false);
  const lastBlur = useRef(0);

  // Initial fetch on mount
  useEffect(() => {
    if (skipInitialFetch) {
      return;
    }
    refetchFn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (!hasBlurred.current) return;
      if (Date.now() - lastBlur.current < blurTimeoutMs) return;

      hasBlurred.current = false;
      refetchFn(true);
    };

    const handleBlur = () => {
      hasBlurred.current = true;
      lastBlur.current = Date.now();

      if (refetchOnBlur) {
        refetchFn(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      } else {
        handleBlur();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchOnBlur, blurTimeoutMs, ...deps]);
}
